'use client';

import React, { useEffect, useRef, useState } from 'react';
import { BRAND_GREEN } from '@voley/shared-types/constants';

interface Props {
  primaryColor: string;
  width?: string | number;
  height?: string | number;
}

export default function ClientCommunicationSvg({ 
  primaryColor, 
  width = "100%", 
  height = 300 
}: Props) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  // Use Intersection Observer to trigger animation when visible
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.intersectionRatio > 0.3) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (svgRef.current) {
      observer.observe(svgRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <style>{`
        @keyframes flowLine {
          0% {
            stroke-dashoffset: 30;
          }
          100% {
            stroke-dashoffset: 0;
          }
        }

        @keyframes flowLineReverse {
          0% {
            stroke-dashoffset: 0;
          }
          100% {
            stroke-dashoffset: 30;
          }
        }

        @keyframes pulseGlow {
          0%, 100% {
            opacity: 0.3;
            filter: blur(0px);
          }
          50% {
            opacity: 0.8;
            filter: blur(2px);
          }
        }

        @keyframes fadeInScale {
          0% {
            opacity: 0;
            transform: scale(0.8);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }

        .connection-line {
          stroke-dasharray: 8, 6;
          stroke-linecap: round;
        }

        .connection-line-animated {
          animation: flowLine 0.8s linear infinite;
        }

        .connection-line-animated-reverse {
          animation: flowLineReverse 0.8s linear infinite;
        }

        .connection-line-glow {
          stroke-dasharray: 8, 6;
          stroke-linecap: round;
          animation: pulseGlow 2s ease-in-out infinite;
        }

        .hub-pulse {
          animation: pulseGlow 3s ease-in-out infinite;
        }

        .fade-in-element {
          opacity: 0;
          transform-origin: center;
        }

        .fade-in-element.visible {
          animation: fadeInScale 0.6s ease-out forwards;
        }

        .fade-in-delay-1 { animation-delay: 0.1s; }
        .fade-in-delay-2 { animation-delay: 0.2s; }
        .fade-in-delay-3 { animation-delay: 0.3s; }
        .fade-in-delay-4 { animation-delay: 0.4s; }
        .fade-in-delay-5 { animation-delay: 0.5s; }
      `}</style>
      <svg 
        ref={svgRef}
        width={width} 
        height={height} 
        viewBox="0 0 500 300" 
        xmlns="http://www.w3.org/2000/svg"
        style={{ overflow: 'visible' }}
      >
        <defs>
          {/* Gradient for glowing lines */}
          <linearGradient id="lineGradient1" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={primaryColor} stopOpacity="0.4" />
            <stop offset="50%" stopColor={primaryColor} stopOpacity="1" />
            <stop offset="100%" stopColor={primaryColor} stopOpacity="0.4" />
          </linearGradient>
          
          {/* Filter for glow effect */}
          <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>

        {/* Connection lines - glow layer (behind) */}
        <g className={isVisible ? 'connection-line-glow' : ''} filter="url(#glow)">
          <line x1="125" y1="100" x2="225" y2="150" stroke={primaryColor} strokeWidth="4" strokeOpacity="0.3" />
          <line x1="125" y1="200" x2="225" y2="150" stroke={primaryColor} strokeWidth="4" strokeOpacity="0.3" />
          <line x1="375" y1="100" x2="275" y2="150" stroke={primaryColor} strokeWidth="4" strokeOpacity="0.3" />
          <line x1="375" y1="200" x2="275" y2="150" stroke={primaryColor} strokeWidth="4" strokeOpacity="0.3" />
        </g>

        {/* Connection lines - animated dashes */}
        {/* Left side lines (team members to hub) - flow towards center */}
        <line 
          x1="125" y1="100" x2="225" y2="150" 
          stroke={primaryColor} 
          strokeWidth="2.5"
          className={`connection-line ${isVisible ? 'connection-line-animated' : ''}`}
          style={{ animationDelay: '0s' }}
        />
        <line 
          x1="125" y1="200" x2="225" y2="150" 
          stroke={primaryColor} 
          strokeWidth="2.5"
          className={`connection-line ${isVisible ? 'connection-line-animated' : ''}`}
          style={{ animationDelay: '0.2s' }}
        />
        
        {/* Right side lines (clients to hub) - flow towards center */}
        <line 
          x1="375" y1="100" x2="275" y2="150" 
          stroke={primaryColor} 
          strokeWidth="2.5"
          className={`connection-line ${isVisible ? 'connection-line-animated-reverse' : ''}`}
          style={{ animationDelay: '0.1s' }}
        />
        <line 
          x1="375" y1="200" x2="275" y2="150" 
          stroke={primaryColor} 
          strokeWidth="2.5"
          className={`connection-line ${isVisible ? 'connection-line-animated-reverse' : ''}`}
          style={{ animationDelay: '0.3s' }}
        />

        {/* Central messaging hub */}
        <circle 
          cx="250" cy="150" r="30" 
          fill={primaryColor} 
          opacity="0.2"
          className={isVisible ? 'hub-pulse' : ''}
        />
        <circle 
          cx="250" cy="150" r="25" 
          fill="white" 
          stroke={primaryColor} 
          strokeWidth="2"
          className={`fade-in-element ${isVisible ? 'visible' : ''}`}
        />
        
        {/* Message icon in center */}
        <path 
          d="M260 139h-20c-2 0-3.5 1.5-3.5 3.5v14c0 2 1.5 3.5 3.5 3.5h3.5v5l5-5h11.5c2 0 3.5-1.5 3.5-3.5v-14c0-2-1.5-3.5-3.5-3.5z" 
          fill={primaryColor}
          className={`fade-in-element ${isVisible ? 'visible' : ''}`}
        />
        
        {/* Team Member 1 */}
        <g className={`fade-in-element fade-in-delay-1 ${isVisible ? 'visible' : ''}`}>
          <circle cx="100" cy="100" r="25" fill="#f5f5f5" stroke="#e0e0e0" strokeWidth="1" />
          <circle cx="100" cy="90" r="10" fill="#dddddd" />
          <rect x="85" y="105" width="30" height="15" rx="7.5" fill="#dddddd" />
        </g>
        
        {/* Team Member 2 */}
        <g className={`fade-in-element fade-in-delay-2 ${isVisible ? 'visible' : ''}`}>
          <circle cx="100" cy="200" r="25" fill="#f5f5f5" stroke="#e0e0e0" strokeWidth="1" />
          <circle cx="100" cy="190" r="10" fill="#dddddd" />
          <rect x="85" y="205" width="30" height="15" rx="7.5" fill="#dddddd" />
        </g>
        
        {/* Client 1 */}
        <g className={`fade-in-element fade-in-delay-3 ${isVisible ? 'visible' : ''}`}>
          <circle cx="400" cy="100" r="25" fill="#f5f5f5" stroke="#e0e0e0" strokeWidth="1" />
          <circle cx="400" cy="90" r="10" fill="#dddddd" />
          <rect x="385" y="105" width="30" height="15" rx="7.5" fill="#dddddd" />
        </g>
        
        {/* Client 2 */}
        <g className={`fade-in-element fade-in-delay-4 ${isVisible ? 'visible' : ''}`}>
          <circle cx="400" cy="200" r="25" fill="#f5f5f5" stroke="#e0e0e0" strokeWidth="1" />
          <circle cx="400" cy="190" r="10" fill="#dddddd" />
          <rect x="385" y="205" width="30" height="15" rx="7.5" fill="#dddddd" />
        </g>

        {/* Animated message particles flowing on lines */}
        {isVisible && (
          <>
            {/* Particle on line 1 (top-left to center) */}
            <circle r="4" fill={primaryColor}>
              <animateMotion
                dur="1.5s"
                repeatCount="indefinite"
                path="M125,100 L225,150"
              />
              <animate attributeName="opacity" values="0;1;1;0" dur="1.5s" repeatCount="indefinite" />
            </circle>
            
            {/* Particle on line 2 (bottom-left to center) */}
            <circle r="4" fill={primaryColor}>
              <animateMotion
                dur="1.8s"
                repeatCount="indefinite"
                path="M125,200 L225,150"
                begin="0.3s"
              />
              <animate attributeName="opacity" values="0;1;1;0" dur="1.8s" repeatCount="indefinite" begin="0.3s" />
            </circle>
            
            {/* Particle on line 3 (top-right to center) */}
            <circle r="4" fill={primaryColor}>
              <animateMotion
                dur="1.6s"
                repeatCount="indefinite"
                path="M375,100 L275,150"
                begin="0.5s"
              />
              <animate attributeName="opacity" values="0;1;1;0" dur="1.6s" repeatCount="indefinite" begin="0.5s" />
            </circle>
            
            {/* Particle on line 4 (bottom-right to center) */}
            <circle r="4" fill={primaryColor}>
              <animateMotion
                dur="2s"
                repeatCount="indefinite"
                path="M375,200 L275,150"
                begin="0.8s"
              />
              <animate attributeName="opacity" values="0;1;1;0" dur="2s" repeatCount="indefinite" begin="0.8s" />
            </circle>

            {/* Reverse particles (center to edges - representing responses) */}
            <circle r="3" fill={BRAND_GREEN}>
              <animateMotion
                dur="1.7s"
                repeatCount="indefinite"
                path="M225,150 L125,100"
                begin="1s"
              />
              <animate attributeName="opacity" values="0;1;1;0" dur="1.7s" repeatCount="indefinite" begin="1s" />
            </circle>
            
            <circle r="3" fill={BRAND_GREEN}>
              <animateMotion
                dur="1.9s"
                repeatCount="indefinite"
                path="M275,150 L375,100"
                begin="1.2s"
              />
              <animate attributeName="opacity" values="0;1;1;0" dur="1.9s" repeatCount="indefinite" begin="1.2s" />
            </circle>
          </>
        )}
      </svg>
    </>
  );
}
